import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Modal, Field, inputCls, btnPrimary } from "./Modal";
import {
  LayoutDashboard, TrendingUp, ShoppingCart, Package, Boxes, Truck, Users, Building2,
  FileText, Wallet, Banknote, BadgePercent, HandCoins, ClipboardCheck, BarChart3,
  UserCheck, History, Settings, Menu, X, Bell, Sun, Moon, LogOut, HardHat, KeyRound, RotateCcw,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de Bord", icon: LayoutDashboard, perm: "dashboard.view", end: true },
  { to: "/pos", label: "Caisse (POS)", icon: ShoppingCart, perm: "pos.use", highlight: true },
  { to: "/ventes", label: "Ventes", icon: TrendingUp, perm: "sales.view" },
  { to: "/retours", label: "Retours", icon: RotateCcw, perm: "returns.view" },
  { to: "/produits", label: "Produits", icon: Package, perm: "products.view" },
  { to: "/stock", label: "Stock", icon: Boxes, perm: "stock.view" },
  { to: "/achats", label: "Achats", icon: Truck, perm: "purchases.view" },
  { to: "/clients", label: "Clients", icon: Users, perm: "customers.view" },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Building2, perm: "suppliers.view" },
  { to: "/documents", label: "Devis & Factures", icon: FileText, perm: "documents.view" },
  { to: "/depenses", label: "Dépenses", icon: Wallet, perm: "expenses.view" },
  { to: "/caisse", label: "Caisse / Trésorerie", icon: Banknote, perm: "cash.view" },
  { to: "/creances", label: "Créances", icon: BadgePercent, perm: "credits.view" },
  { to: "/dettes", label: "Dettes", icon: HandCoins, perm: "debts.view" },
  { to: "/inventaire", label: "Inventaire", icon: ClipboardCheck, perm: "inventory.manage" },
  { to: "/rapports", label: "Rapports", icon: BarChart3, perm: "reports.view" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCheck, perm: "users.manage" },
  { to: "/audit", label: "Journal d'Audit", icon: History, perm: "audit.view" },
  { to: "/parametres", label: "Paramètres", icon: Settings, perm: "settings.manage" },
];

export default function Layout() {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });

  const changePw = async () => {
    if (pw.new_password !== pw.confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    try {
      await api.post("/auth/change-password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Mot de passe modifié");
      setPwOpen(false);
      setPw({ old_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const fetchNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    fetchNotifs();
    const t = setInterval(fetchNotifs, 60000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const visibleNav = NAV.filter((n) => hasPerm(n.perm));

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 h-16 border-b shrink-0">
        {user?.tenant_logo ? (
          <img data-testid="sidebar-logo" src={fileUrl(user.tenant_logo)} alt="logo" className="h-9 w-9 rounded-lg object-contain bg-white border" />
        ) : (
          <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center">
            <HardHat className="h-5 w-5 text-accent-foreground" />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-heading font-bold text-sm leading-tight truncate" data-testid="sidebar-store-name">{user?.tenant_name || "Quincaillerie"}</div>
          <div className="text-[11px] text-muted-foreground">Gestion commerciale</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {visibleNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              } ${n.highlight ? "border border-accent/40" : ""}`}>
            <n.icon className="h-4.5 w-4.5 h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t p-3 space-y-2 shrink-0">
        <div className="flex items-center gap-2 px-1">
          <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
            {(user?.name || "?")[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <button data-testid="change-password-btn" onClick={() => setPwOpen(true)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <KeyRound className="h-4 w-4" /> Changer mon mot de passe
        </button>
        <button data-testid="logout-button" onClick={doLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 transition-colors">
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-card border-r z-30">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-card border-r">{sidebar}</aside>
        </div>
      )}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 h-16 bg-card/80 backdrop-blur border-b flex items-center gap-3 px-4">
          <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-lg border">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex-1" />
          <div className="relative">
            <button data-testid="notif-bell" onClick={() => setShowNotifs(!showNotifs)}
              className="h-10 w-10 inline-flex items-center justify-center rounded-lg border hover:bg-muted relative">
              <Bell className="h-5 w-5" />
              {notifs.length > 0 && (
                <span data-testid="notif-count" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {notifs.length}
                </span>
              )}
            </button>
            {showNotifs && (
              <div data-testid="notif-panel" className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-popover border rounded-xl shadow-xl p-2 space-y-1">
                {notifs.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Aucune notification</div>}
                {notifs.map((n, i) => (
                  <div key={i} className={`px-3 py-2 rounded-lg text-xs font-medium ${
                    n.severity === "danger" ? "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300"
                    : n.severity === "warning" ? "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                    : "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"}`}>
                    {n.message}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button data-testid="theme-toggle" onClick={() => setDark(!dark)}
            className="h-10 w-10 inline-flex items-center justify-center rounded-lg border hover:bg-muted">
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title="Changer mon mot de passe">
        <div className="space-y-3">
          <Field label="Ancien mot de passe"><input data-testid="pw-old" type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} className={inputCls} /></Field>
          <Field label="Nouveau mot de passe"><input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} /></Field>
          <Field label="Confirmer"><input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} /></Field>
          <button data-testid="pw-submit" onClick={changePw} disabled={!pw.old_password || pw.new_password.length < 6} className={`${btnPrimary} w-full`}>Modifier</button>
        </div>
      </Modal>
    </div>
  );
}
